import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {Subscription} from 'rxjs';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {Sorting} from 'features/core/shared/misc/sorting';
import {QlQueryDto} from 'features/core/shared/ql/data/dto/ql-query.dto';
import {MvsWidgetComponent} from 'features/core/shared/widget/component/runtime/mvs-widget/mvs-widget.component';
import {MvsObjectService} from 'features/core/shared/service/object/mvs-object.service';
import {JbSpringBatchIndicatorService} from 'features/feature-tech/jb/service/jb-spring-batch-indicator.service';

/**
 * navItem "Runs/Deckung" of the ProvisionAgent OBC (04-frontend.md §7), widget alias
 * `cp.agent.reserveCoverage.table`.
 *
 * Shows the `cp.ProvisionAgentReserveCoverage` rows (R-05) of the agent: for each provision run
 * the open liability, the reserve balance at calculation time, the coverage ratio, and —
 * most important from a business perspective — the `shortfallAmount`, i.e. `max(0, open liability − reserve)`.
 * A `shortfallAmount > 0` is the metric behind the payout brake (U-03).
 *
 * Together with "Auszahlungen pro Monat" this navItem thereby covers the requirement wording
 * "data on the BillingRuns" (B-F-15 interpretation, 04 §7): meaning the provision runs
 * and the payouts, not the bm billing runs.
 *
 * QL shape following the repo example `bm-billing-analytics.component.ts`.
 *
 * REFRESH (04-frontend.md §4), following the pattern of the balance pane
 * {@link CpProvisionAgentBalanceComponent}: subscription to `JbSpringBatchIndicatorService.status$`
 * AND to `MvsObjectService.objectChangeEvent`. The first subscription is the decisive one here: the
 * reserve coverage rows are CREATED in the post-processing of the calculation run (03 §5.9 c) and are
 * updated by the balance job — without this subscription the table would keep showing, after a run,
 * the coverage from before, i.e. exactly the `shortfallAmount` that justified the payout brake.
 * Reloaded on the running -> not-running transition, in place via
 * `MvsWidgetComponent.refreshWidget()`, so sorting and scroll position are preserved.
 */
@Component({
    selector: 'cp-provision-agent-reserve-coverage',
    templateUrl: './cp-provision-agent-reserve-coverage.component.html',
    styleUrls: ['./cp-provision-agent-reserve-coverage.component.scss'],
    standalone: false
})
export class CpProvisionAgentReserveCoverageComponent implements OnInit, OnChanges, OnDestroy {

    busy: boolean;
    initialized: boolean;

    @Input() provisionAgentId: number;

    reserveCoverageWidget: WidgetData;

    /** Is a cp job currently running? */
    jobRunning: boolean;

    /**
     * The mounted widget instance — only for the in-place reload. `refreshWidget()` reloads,
     * WITHOUT touching the binding; a freshly re-assigned `reserveCoverageWidget` would remount
     * the table and lose sorting and scroll position (same rationale as in
     * `cp-provision-agents.component.ts`).
     */
    @ViewChild('reserveCoverageWidgetRef') reserveCoverageWidgetComponent: MvsWidgetComponent;

    protected jobWasRunning: boolean;
    protected statusSubscription: Subscription;
    protected objectChangeSubscription: Subscription;

    constructor(
        protected mvsObjectService: MvsObjectService,
        protected springBatchIndicatorService: JbSpringBatchIndicatorService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    initComponent(): void {
        this.subscribeToObjectChanges();
        this.subscribeToJobCompletion();
        this.refreshComponent();
    }

    /**
     * Reloads as soon as a coverage row was written. Additionally listens to account
     * bookings: a manual transfer into the reserve account changes the coverage that this pane
     * is built on.
     */
    protected subscribeToObjectChanges(): void {
        this.objectChangeSubscription = this.mvsObjectService.objectChangeEvent.subscribe(change => {
            if (change.objectType === 'cp.ProvisionAgentReserveCoverage'
                || change.objectType === 'cp.ProvisionAgentTransaction') {
                this.reloadInPlace();
            }
        });
    }

    /** Subscription to the job indicator; reload on the running -> not-running transition (04 §4). */
    protected subscribeToJobCompletion(): void {
        this.statusSubscription = this.springBatchIndicatorService.status$.subscribe(status => {
            const wasRunning: boolean = this.jobRunning;

            this.jobRunning = status.hasRunningJobs;

            if (status.hasRunningJobs) {
                this.jobWasRunning = true;
            } else if (this.jobWasRunning && wasRunning && !status.hasRunningJobs) {
                this.jobWasRunning = false;
                this.reloadInPlace();
            }
        });
    }

    /** Reloads the table in place; builds one only if none exists yet. */
    protected reloadInPlace(): void {
        if (this.reserveCoverageWidgetComponent) {
            this.reserveCoverageWidgetComponent.refreshWidget();
            return;
        }
        this.refreshComponent();
    }

    refreshComponent(): void {

        if (!this.provisionAgentId) {
            this.reserveCoverageWidget = null;
            this.initialized = true;
            return;
        }

        const qlQuery: QlQueryDto = {
            name: 'cp.agent.reserveCoverage',
            type: 'view',
            start: {
                type: 'entity',
                name: 'cp.ProvisionAgentReserveCoverage',
                as: 'e',
                joins: [
                    {
                        type: 'entity', name: 'provisionRun', as: 'pr', joinType: 'left',
                        joins: [
                            {type: 'entity', name: 'provisionType', as: 'pt', joinType: 'left'}
                        ]
                    }
                ],
                parameters: [
                    {param: 'resolve_fkey_texts', value: 'true'}
                ]
            },
            fixedFilters: [
                FilterCriteria.create('e.provisionAgentId', FilterCriteria.cOperatorEqual, this.provisionAgentId)
            ],
            sortings: [new Sorting('e.provisionRunId', false)]
        };

        /**
         * AI: Reserve coverage trend of an agent per provision run.
         *     Source: QL view on cp.ProvisionAgentReserveCoverage, join provision run and type.
         *     Caseworkers see from shortfallAmount whether the payout is being throttled.
         */
        this.reserveCoverageWidget = WidgetFactory.createWidgetTableQl(
            'cp.agent.reserveCoverage.table',
            'Deckung je Provisionslauf',
            qlQuery,
            WidgetDataParam.create('paging', 25),
            WidgetDataParam.create('size', 'S')
        );

        this.initialized = true;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['provisionAgentId']) {
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
        this.objectChangeSubscription?.unsubscribe();
        this.statusSubscription?.unsubscribe();
    }

}
